/**
 * X3DH — the asynchronous handshake that establishes a shared secret with a
 * recipient who is offline, using the prekey bundle they left with the server.
 *
 * Reference: https://signal.org/docs/specifications/x3dh/
 */
import {
  type KeyPair,
  concatBytes,
  dh,
  identityPubToDH,
  identityToDH,
  kdf,
  KEY_LEN,
} from './primitives.ts';

const X3DH_INFO = 'VAV-X3DH-v1';

/**
 * The 32 0xFF bytes prepended to the DH concatenation. This is what keeps the
 * X25519 output domain distinct from the Curve448 variant in the spec, and more
 * usefully here, distinct from any other HKDF use in this codebase.
 */
const F = new Uint8Array(32).fill(0xff);

/**
 * Associated data mixed into every message of the session: both identity keys,
 * initiator first. Binding it means a ciphertext cannot be replayed into a
 * different pair of participants even if the keys somehow collided.
 */
export function associatedData(initiatorIdPub: Uint8Array, responderIdPub: Uint8Array): Uint8Array {
  return concatBytes(initiatorIdPub, responderIdPub);
}

export interface X3DHInitiatorResult {
  sharedSecret: Uint8Array;
  ephemeral: KeyPair;
  associatedData: Uint8Array;
}

export interface InitiatorInput {
  /** Our long-term Ed25519 identity. */
  identity: KeyPair;
  ephemeral: KeyPair;
  /** Their Ed25519 identity key, as published. */
  theirIdentityPub: Uint8Array;
  /** Their signed prekey — caller must have verified the signature already. */
  theirSignedPreKeyPub: Uint8Array;
  /** Their one-time prekey, when the server still had one to give. */
  theirOneTimePreKeyPub?: Uint8Array;
}

/**
 * Sender side. Produces the shared secret plus the ephemeral key that must be
 * shipped in the PreKeyMessage so the recipient can reproduce it.
 */
export function x3dhInitiate(input: InitiatorInput): X3DHInitiatorResult {
  const idDH = identityToDH(input.identity);
  const theirIdDH = identityPubToDH(input.theirIdentityPub);

  // DH1 binds our identity to their signed prekey  -> authenticates us.
  const dh1 = dh(idDH.priv, input.theirSignedPreKeyPub);
  // DH2 binds our ephemeral to their identity      -> authenticates them.
  const dh2 = dh(input.ephemeral.priv, theirIdDH);
  // DH3 gives forward secrecy from the signed prekey.
  const dh3 = dh(input.ephemeral.priv, input.theirSignedPreKeyPub);

  let ikm = concatBytes(F, dh1, dh2, dh3);
  if (input.theirOneTimePreKeyPub) {
    // DH4 upgrades forward secrecy: this key is deleted after one use, so a
    // later compromise of the signed prekey does not open this session.
    ikm = concatBytes(ikm, dh(input.ephemeral.priv, input.theirOneTimePreKeyPub));
  }

  const sharedSecret = kdf(ikm, new Uint8Array(32), X3DH_INFO, KEY_LEN);
  return {
    sharedSecret,
    ephemeral: input.ephemeral,
    associatedData: associatedData(input.identity.pub, input.theirIdentityPub),
  };
}

export interface ResponderInput {
  identity: KeyPair;
  signedPreKey: KeyPair;
  /** The one-time prekey the sender claimed, if any. */
  oneTimePreKey?: KeyPair;
  theirIdentityPub: Uint8Array;
  theirEphemeralPub: Uint8Array;
}

/**
 * Recipient side. Mirrors `x3dhInitiate` with the DH arguments swapped; any
 * mismatch (wrong prekey id, tampered ephemeral) yields a different secret and
 * the first message simply fails to authenticate.
 */
export function x3dhRespond(input: ResponderInput): { sharedSecret: Uint8Array; associatedData: Uint8Array } {
  const idDH = identityToDH(input.identity);
  const theirIdDH = identityPubToDH(input.theirIdentityPub);

  const dh1 = dh(input.signedPreKey.priv, theirIdDH);
  const dh2 = dh(idDH.priv, input.theirEphemeralPub);
  const dh3 = dh(input.signedPreKey.priv, input.theirEphemeralPub);

  let ikm = concatBytes(F, dh1, dh2, dh3);
  if (input.oneTimePreKey) {
    ikm = concatBytes(ikm, dh(input.oneTimePreKey.priv, input.theirEphemeralPub));
  }

  return {
    sharedSecret: kdf(ikm, new Uint8Array(32), X3DH_INFO, KEY_LEN),
    associatedData: associatedData(input.theirIdentityPub, input.identity.pub),
  };
}
